import React, { useMemo } from "react";
import {
  Receipt,
  FileText,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  ShieldCheck,
  Wallet,
  CreditCard,
  Hash,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useGetMyPaymentsQuery } from "../../api/billingApi";
import { formatCurrency, formatDate } from "../../../../utils/formatters";
export default function MyPayments() {
  const { data: payments, isLoading } = useGetMyPaymentsQuery();

  console.log(payments, "th3ese are the paymentss");

  // ✅ Summary Stats Calculation
  const totalPaid = useMemo(() => {
    if (!payments) return 0;
    return payments
      ?.filter((pay: any) => pay?.status == "paid")
      .reduce((sum: number, p: any) => {
        const amt = parseFloat(p?.amount);
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0);
  }, [payments]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex flex-col items-center justify-center min-h-[400px]">
        <CreditCard className="w-12 h-12 text-indigo-500 animate-pulse mb-4" />
        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-400">
          Syncing Transaction Ledger...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-4 md:p-6 pb-20">
      {/* 1. Industrial Header */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Receipt className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                Payment Registry
              </h1>
              <p className="text-gray-400 text-sm mt-2 font-medium">
                Official transaction history and remittance logs.
              </p>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="flex gap-4 w-full md:w-auto">
            <div className="bg-gray-50 rounded-3xl p-5 border border-gray-100 flex-1 md:min-w-[240px]">
              <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-2">
                <Wallet className="w-3 h-3" /> Total Remitted
              </div>
              <div className="text-xl font-black text-gray-900 tracking-tighter">
                {formatCurrency(totalPaid)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Payments List Matrix */}
      {!payments?.length ? (
        <div className="bg-white border border-gray-100 rounded-[3rem] p-24 text-center">
          <CreditCard className="w-16 h-16 mx-auto text-gray-100 mb-6" />
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-300">
            No remittance records found in your registry
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {payments
            ?.filter((pay: any) => pay?.status == "paid")
            ?.map((p: any) => (
              <div
                key={p.id}
                className="group bg-white border border-gray-100 rounded-[2.2rem] p-2 hover:shadow-xl hover:shadow-gray-100 transition-all duration-500 overflow-hidden"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6 p-4 md:p-6">
                  {/* Amount & Method Section */}
                  <div className="flex-1 flex items-center gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-black text-gray-900 uppercase tracking-tight text-xl leading-none">
                          {formatCurrency(p.amount)}
                        </h3>
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-100 bg-emerald-50 text-emerald-700 text-[9px] font-black uppercase tracking-widest">
                          Settled
                        </div>
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 mt-2 uppercase tracking-widest flex items-center gap-2">
                        <Clock className="w-3 h-3" /> {formatDate(p.date)} •{" "}
                        {p.gateway?.replace("_", " ") || "DIRECT"}
                      </div>
                    </div>
                  </div>

                  {/* Audit Identifiers Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 px-2 md:px-8 md:border-x border-gray-50">
                    <div className="space-y-1">
                      <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest flex items-center gap-1">
                        <Hash className="w-2 h-2" /> Receipt No.
                      </div>
                      <div className="font-mono font-bold text-gray-900 text-sm tracking-tight">
                        {p.receipt_number || "---"}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
                        Audit Reference
                      </div>
                      <div className="font-mono text-[10px] text-gray-500 truncate max-w-[120px]">
                        {p.reference}
                      </div>
                    </div>
                  </div>

                  {/* Action Section */}
                  <div className="flex items-center justify-end">
                    <Link
                      to={`/receipt/${p.reference}`}
                      className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95 group/btn"
                    >
                      View Receipt
                      <ArrowUpRight className="w-3 h-3 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                    </Link>
                  </div>
                </div>

                {/* 3. Items Breakdown (The "Allocation" matrix) */}
                {p.items && p.items.length > 0 && (
                  <div className="bg-gray-50/50 mx-4 mb-4 rounded-3xl p-6 border border-gray-100">
                    <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-4">
                      Items Breakdown
                    </div>
                    <div className="space-y-3">
                      {p.items.map((item: any, index: number) => (
                        <div
                          key={index}
                          className={`flex justify-between items-center text-xs border-b pb-2 last:border-0 last:pb-0 ${item.is_advance ? "border-emerald-100 bg-emerald-50/60 rounded-xl px-3 py-2" : "border-gray-100"}`}
                        >
                          <span className={`font-bold uppercase flex items-center gap-2 ${item.is_advance ? "text-emerald-700" : "text-gray-600"}`}>
                            <div className={`w-1 h-1 rounded-full ${item.is_advance ? "bg-emerald-400" : "bg-indigo-400"}`} />
                            {item.description || `Item ${index + 1}`}
                            {item.is_advance && (
                              <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                credit
                              </span>
                            )}
                          </span>
                          <span className={`font-black font-mono ${item.is_advance ? "text-emerald-700" : "text-gray-900"}`}>
                            {formatCurrency(item.amount_paid || item.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* 4. Industrial Footer Notification */}
      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl shadow-gray-200 mt-10 overflow-hidden relative border border-gray-800">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none text-emerald-500">
          <ShieldCheck className="w-32 h-32" />
        </div>
        <div className="w-20 h-20 bg-emerald-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-emerald-500/20">
          <ShieldCheck className="w-10 h-10 text-emerald-400" />
        </div>
        <div className="relative z-10">
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-emerald-400 mb-3">
            Payment Verification Protocol
          </h4>
          <p className="text-sm text-gray-400 leading-relaxed max-w-2xl font-medium">
            This ledger contains all successful remittances processed via the{" "}
            <span className="text-white font-bold">NIQS GATEWAY</span>. Each
            entry is verified against banking protocols. If a payment is not
            reflected here after <span className="text-white">24 HOURS</span>,
            please present your{" "}
            <span className="text-white">AUDIT REFERENCE</span> to the treasury
            department.
          </p>
        </div>
      </div>
    </div>
  );
}
